import * as React from 'react';
import * as styles from "./RecurrenceSettings.css";
import { inject, observer } from 'mobx-react';
import { Store } from '../../stores';
import { LocalizationService } from '../../../../services/LocalizationService';
import { IANumberField } from '../../../numberField/NumberField';
import { IADropDown } from '../../../dropDown/DropDown';
import { IAButton } from '../../../button/Button';
import { IAChoiceGroup } from '../../../choiceGroup/ChoiceGroup';
import { IADateAndTimePicker } from '../../../dateAndTimePicker/DateAndTimePicker';
import { MonthlyPatternType, RecurrenceDataDayOfWeek, RecurrenceDataType } from '../../../../interfaces/IRecurrenceData';
import { RecurrenceHelper, RecurrenceEndType } from '../../../../RecurrenceHelper';

export interface IRecurrenceSettingsProps {
  store?: Store;
  onChange(): void;
}

@inject('store')
@observer
export default class RecurrenceSettings extends React.Component<IRecurrenceSettingsProps> {
  private localizationService: LocalizationService = new LocalizationService();

  constructor(props: IRecurrenceSettingsProps) {
    super(props);
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public render(): JSX.Element {
    return (
      <div className={styles.IA_recurrenceSettingsWrapper}>
        <div className={styles.IA_repeatEveryWrapper}>
          <p>
            {this.localizationService.strings.Events_RecurrenceRepeatEvery}
          </p>
          <IANumberField
            number={this.props.store.recurrenceInfo?.recurrenceData.interval}
            onChange={number => {
              this.props.store.recurrenceInfo.recurrenceData.interval = number;
              this.props.onChange();
            }}
            borderRadius={5}
            highlightColor={this.props.store.highlightColor}
            styles={{
              marginTop: 0,
              marginLeft: 10,
              width: 50
            }}
            min={1}
          />
          <IADropDown
            onChange={(option: RecurrenceDataType) => {
              this.props.store.recurrenceInfo.recurrenceData.recurrenceType = option;
              this.props.onChange();
            }}
            options={[
              {
                key: "daily",
                value: this.localizationService.strings.Events_RecurrenceDays
              },
              {
                key: "weekly",
                value: this.localizationService.strings.Events_RecurrenceWeeks
              },
              {
                key: "monthly",
                value: this.localizationService.strings.Events_RecurrenceMonths
              },
              {
                key: "yearly",
                value: this.localizationService.strings.Events_RecurrenceYears
              }
            ]}
            selectedOption={this.props.store.recurrenceInfo?.recurrenceData.recurrenceType}
            borderRadius={5}
            highlightColor={this.props.store.highlightColor}
            style={{
              marginTop: 0,
              marginLeft: 10,
              width: 100
            }}
          />
        </div>
        {this.props.store.recurrenceInfo?.recurrenceData?.recurrenceType === "weekly" &&
          <>
            <p className={styles.IA_repeatWeeklyP}>
              {this.localizationService.strings.Events_RecurrenceRepeatOn}
            </p>
            <div className={styles.IA_repeatWeeklyButtons}>
              {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day: RecurrenceDataDayOfWeek) => {
                const isSelected = this.props.store.recurrenceInfo?.recurrenceData?.daysOfWeek?.find(x => x === day);
                return <IAButton
                  style={{
                    padding: 0,
                    width: 30,
                    marginRight: 4
                  }}
                  label={RecurrenceHelper.getLocalizedDay(day, this.localizationService)[0]}
                  borderRadius={500}
                  textColor={isSelected ? "white" : this.props.store.highlightColor}
                  buttonColor={isSelected ? this.props.store.highlightColor : "#eee"}
                  onClick={() => {
                    if (isSelected) {
                      this.props.store.recurrenceInfo?.recurrenceData.daysOfWeek.splice(
                        this.props.store.recurrenceInfo?.recurrenceData.daysOfWeek.findIndex(x => x === day),
                        1
                      );
                    } else {
                      this.props.store.recurrenceInfo?.recurrenceData.daysOfWeek.push(day);
                    }
                    this.props.onChange();
                  }}
                />
              })}
            </div>
          </>
        }
        {this.props.store.recurrenceInfo?.recurrenceData?.recurrenceType === "monthly" &&
          <>
            <IADropDown
              onChange={(option: MonthlyPatternType) => {
                const patternOptions = RecurrenceHelper.getMonthlyRecurrencePatternTypes(this.props.store.startDate, this.localizationService).find(x => x.monthlyPattern === option);
                this.props.store.recurrenceInfo.recurrenceData.monthlyPattern = patternOptions.monthlyPattern;
                this.props.store.recurrenceInfo.recurrenceData.weekNumberInMonth = patternOptions.weekNumberInMonth;
                this.props.store.recurrenceInfo.recurrenceData.dayOfWeekInMonth = patternOptions.dayOfWeekInMonth;
                this.props.onChange();
              }}
              options={RecurrenceHelper.getMonthlyRecurrencePatternTypes(this.props.store.startDate, this.localizationService).map(option => {
                return {
                  key: option.monthlyPattern,
                  value: option.text
                };
              })}
              selectedOption={this.props.store.recurrenceInfo?.recurrenceData.monthlyPattern}
              borderRadius={5}
              highlightColor={this.props.store.highlightColor}
              style={{
                marginTop: 0,
                width: 252
              }}
            />
          </>
        }
        <p style={{ marginTop: 10, marginBottom: 5 }}>{this.localizationService.strings.Events_RecurrenceEnds}</p>
        <IAChoiceGroup
          highlightColor={this.props.store.highlightColor}
          options={this.props.store.component === "Event" ? [
            {
              key: "never",
              text: this.localizationService.strings.Events_RecurrenceNever
            },
            {
              key: "date",
              text: this.localizationService.strings.Events_RecurrenceOnDate
            },
            {
              key: "occurrences",
              text: this.localizationService.strings.Events_RecurrenceAfterOccurrences
            }
          ] : [
            {
              key: "never",
              text: this.localizationService.strings.Events_RecurrenceNever
            },
            {
              key: "date",
              text: this.localizationService.strings.Events_RecurrenceOnDate
            }
          ]}
          onChange={(key: RecurrenceEndType) => {
            switch (key) {
              case "never":
                this.props.store.recurrenceInfo.recurrenceData.repeatForever = true;
                break;
              case "date":
                const endDate = new Date();
                endDate.setMonth(endDate.getMonth() + 1);
                this.props.store.recurrenceInfo.recurrenceData.repeatForever = false;
                this.props.store.recurrenceInfo.recurrenceData.endDate = endDate;
                this.props.store.recurrenceInfo.recurrenceData.occurrenceCount = undefined;
                break;
              case "occurrences":
                this.props.store.recurrenceInfo.recurrenceData.repeatForever = false;
                this.props.store.recurrenceInfo.recurrenceData.occurrenceCount = 10;
                this.props.store.recurrenceInfo.recurrenceData.endDate = undefined;
              default:
                break;
            }
            this.props.onChange();
          }}
          required
          selectedKey={RecurrenceHelper.getRecurrenceEndType(this.props.store.recurrenceInfo?.recurrenceData)}
          style={{
            marginBottom: 5
          }}
        />
        {RecurrenceHelper.getRecurrenceEndType(this.props.store.recurrenceInfo?.recurrenceData) === "date" &&
          <IADateAndTimePicker
            selectedDate={this.props.store.recurrenceInfo?.recurrenceData.endDate}
            onChange={(date) => {
              this.props.store.recurrenceInfo.recurrenceData.endDate = date;
              this.props.onChange();
            }}
            showTimeSelector
            highlightColor={this.props.store.highlightColor}
            borderRadius={5}
            style={{
              marginTop: -8
            }}

          />
        }
        {RecurrenceHelper.getRecurrenceEndType(this.props.store.recurrenceInfo?.recurrenceData) === "occurrences" &&
          <IANumberField
            number={this.props.store.recurrenceInfo?.recurrenceData.occurrenceCount}
            onChange={number => {
              this.props.store.recurrenceInfo.recurrenceData.occurrenceCount = number;
              this.props.onChange();
            }}
            borderRadius={5}
            highlightColor={this.props.store.highlightColor}
            styles={{
              marginTop: 0,
              width: 150
            }}
            min={1}
          />
        }
      </div>
    );
  }
}